import React, { useEffect, useRef } from 'react';
import './CBCTGeomCalculator.css';

function CBCTGeomCalculator() {
  const rootRef = useRef(null);

  useEffect(() => {
    document.title = 'CTViz | CBCT Geometry Calculator';
    return () => { document.title = 'CTViz'; };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let volumeLocked = true;

    function syncVolumePeer(sourceId) {
      if (!volumeLocked) return;
      if (sourceId !== 'vw' && sourceId !== 'vh') return;
      const peerId = sourceId === 'vw' ? 'vh' : 'vw';
      const srcInp  = document.getElementById('inp-' + sourceId);
      const peerInp = document.getElementById('inp-' + peerId);
      const peerWrap = peerInp.closest('.drag-input-wrap');
      const peerFill = peerWrap.querySelector('.drag-fill');
      const peerMin  = parseFloat(peerWrap.dataset.min);
      const peerMax  = parseFloat(peerWrap.dataset.max);
      const peerDec  = parseInt(peerWrap.dataset.decimals || '0');
      peerInp.value  = parseFloat(srcInp.value).toFixed(peerDec);
      const v   = parseFloat(peerInp.value) || 0;
      const pct = Math.max(0, Math.min(100, ((v - peerMin) / (peerMax - peerMin)) * 100));
      peerFill.style.width = pct + '%';
    }

    // Declare update before drag setup — assigned below after helpers are defined
    let update;

    const cleanupFns = [];

    root.querySelectorAll('.drag-input-wrap').forEach(wrap => {
      const inp  = wrap.querySelector('input');
      const fill = wrap.querySelector('.drag-fill');
      const id   = wrap.dataset.id;
      const min  = parseFloat(wrap.dataset.min);
      const max  = parseFloat(wrap.dataset.max);
      const dec  = parseInt(wrap.dataset.decimals || '2');

      let dragging = false, pendingDrag = false, startX = 0, startVal = 0;

      function updateFill() {
        const v   = parseFloat(inp.value) || 0;
        const pct = Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
        fill.style.width = pct + '%';
      }

      function clamp(v) {
        return Math.max(min, Math.min(max, parseFloat(v.toFixed(dec))));
      }

      function onMouseDown(e) {
        if (document.activeElement === inp) return;
        e.preventDefault();
        pendingDrag = true;
        dragging    = false;
        startX      = e.clientX;
        startVal    = parseFloat(inp.value) || 0;
        wrap.classList.add('active');
        document.body.classList.add('cbct-dragging');
      }

      function onMouseMove(e) {
        if (!dragging && !pendingDrag) return;
        const dx = e.clientX - startX;
        if (pendingDrag && Math.abs(dx) > 3) {
          dragging    = true;
          pendingDrag = false;
        }
        if (!dragging) return;
        const sensitivity = (max - min) / 300;
        const newVal = clamp(startVal + dx * sensitivity);
        inp.value = newVal.toFixed(dec);
        syncVolumePeer(id);
        updateFill();
        if (update) update();
      }

      function onMouseUp() {
        if (pendingDrag) {
          pendingDrag = false;
          wrap.classList.remove('active');
          document.body.classList.remove('cbct-dragging');
          inp.focus();
          inp.select();
        }
        if (dragging) {
          dragging = false;
          wrap.classList.remove('active');
          document.body.classList.remove('cbct-dragging');
        }
      }

      inp.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);

      inp.addEventListener('focus', () => { inp.style.cursor = 'text'; wrap.style.cursor = 'text'; });
      inp.addEventListener('blur',  () => {
        inp.style.cursor  = '';
        wrap.style.cursor = '';
        syncVolumePeer(id);
        updateFill();
        if (update) update();
      });
      inp.addEventListener('input', () => {
        syncVolumePeer(id);
        updateFill();
        if (update) update();
      });
      inp.addEventListener('keydown', e => { if (e.key === 'Enter') inp.blur(); });

      updateFill();

      cleanupFns.push(() => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      });
    });

    function setVolumeLock(locked) {
      volumeLocked = locked;
      [document.getElementById('btn-lock-vwh'), document.getElementById('btn-lock-vwh-mid')].forEach(btn => {
        btn.classList.toggle('locked', locked);
        btn.textContent = locked ? '🔒' : '🔓';
      });
      if (locked) {
        syncVolumePeer('vw');
        if (update) update();
      }
    }

    document.getElementById('btn-lock-vwh').addEventListener('click',     () => setVolumeLock(!volumeLocked));
    document.getElementById('btn-lock-vwh-mid').addEventListener('click', () => setVolumeLock(!volumeLocked));

    function gv(id) { return parseFloat(document.getElementById('inp-' + id).value) || 0; }

    function getParams() {
      const sod     = gv('sod');
      const sdd     = gv('sdd');
      const tiltDeg = gv('tilt');
      const tilt    = tiltDeg * Math.PI / 180;
      const dpw     = gv('dpw');
      const dph     = gv('dph');
      const pxsz    = gv('pxsz');
      const mag     = sdd / sod;
      const voxMM   = pxsz / mag;
      const vwPx    = gv('vw');
      const vhPx    = gv('vh');
      const vzPx    = gv('vz_size');
      const vwMM    = vwPx * voxMM;
      const vhMM    = vhPx * voxMM;
      const vzMM    = vzPx * voxMM;
      const cx      = gv('cx');
      const cy      = gv('cy');
      const cz      = gv('cz');
      const hOff    = gv('hoff');
      const vOff    = gv('voff');
      return { sod, sdd, tilt, tiltDeg, dpw, dph, pxsz, mag, voxMM,
               vwPx, vhPx, vzPx,
               vrMM: vwMM/2, vhMM: vhMM/2, vzMM: vzMM/2,
               cx, cy, cz,
               dwMM: dpw*pxsz, dhMM: dph*pxsz,
               hOff, vOff,
               hOff_mm: hOff * pxsz, vOff_mm: vOff * pxsz };
    }

    function compute(p) {
      const { sod, sdd, tilt, pxsz, mag, voxMM, vrMM, vhMM, vzMM,
              cx, cy, cz, dwMM, dhMM, hOff_mm, vOff_mm } = p;

      const fovR    = dwMM / 2 / mag;
      const fovH    = dhMM / 2 / mag;
      const tiltOff = sod * Math.tan(tilt);
      const coneAng = Math.atan(fovR / sod);

      const sinT = Math.sin(tilt), cosT = Math.cos(tilt);
      const cxT  = cx*cosT + cz*sinT;
      const cyT  = cy;
      const czT  = -cx*sinT + cz*cosT;
      const srcZ = -sod;
      const detZ = sdd - sod;

      function projectPt(tx, ty, tz) {
        const d = tz - srcZ;
        if (d <= 0) return null;
        const s = (detZ - srcZ) / d;
        return { u: tx*s, v: ty*s };
      }

      let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
      let allInside = true;
      const Nr = 36;

      for (let i = 0; i < Nr; i++) {
        const a = (i/Nr) * 2 * Math.PI;
        for (const dz of [-vzMM, 0, vzMM]) {
          const ox2 = cx + vrMM*Math.cos(a);
          const oy2 = cy + (dz === 0 ? 0 : Math.sign(dz)*vhMM);
          const oz2 = cz + vrMM*Math.sin(a) + dz;
          const tx  = ox2*cosT + oz2*sinT;
          const ty  = oy2;
          const tz  = -ox2*sinT + oz2*cosT;
          const pt  = projectPt(tx, ty, tz);
          if (!pt) { allInside = false; continue; }
          if (Math.abs(pt.u - hOff_mm) > dwMM/2 || Math.abs(pt.v - vOff_mm) > dhMM/2) allInside = false;
          minU = Math.min(minU, pt.u); maxU = Math.max(maxU, pt.u);
          minV = Math.min(minV, pt.v); maxV = Math.max(maxV, pt.v);
        }
      }

      for (const dz of [-vzMM, vzMM]) {
        for (const dxy of [[vrMM,0],[0,vhMM],[-vrMM,0],[0,-vhMM]]) {
          const ox = cx+dxy[0], oy = cy+dxy[1], oz = cz+dz;
          const tx = ox*cosT+oz*sinT, ty = oy, tz = -ox*sinT+oz*cosT;
          const pt = projectPt(tx, ty, tz);
          if (!pt) { allInside = false; continue; }
          if (Math.abs(pt.u - hOff_mm) > dwMM/2 || Math.abs(pt.v - vOff_mm) > dhMM/2) allInside = false;
          minU = Math.min(minU, pt.u); maxU = Math.max(maxU, pt.u);
          minV = Math.min(minV, pt.v); maxV = Math.max(maxV, pt.v);
        }
      }

      const projWpx = isFinite(maxU-minU) ? (maxU-minU)/pxsz : 0;
      const projHpx = isFinite(maxV-minV) ? (maxV-minV)/pxsz : 0;
      const projZpx = (vzMM*2 * mag) / pxsz;

      return {
        fovR, fovH, tiltOff, coneAng, allInside,
        projWpx, projHpx, projZpx,
        volWvox: vrMM*2/voxMM, volHvox: vhMM*2/voxMM, volZvox: vzMM*2/voxMM,
        mL: (hOff_mm + dwMM/2 - maxU) / pxsz,
        mR: (minU + dwMM/2 - hOff_mm) / pxsz,
        mT: (vOff_mm + dhMM/2 - maxV) / pxsz,
        mB: (minV + dhMM/2 - vOff_mm) / pxsz,
        minU, maxU, minV, maxV,
        cProj: projectPt(cxT, cyT, czT),
      };
    }

    function resizeCanvas(canvas) {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr  = window.devicePixelRatio || 1;
      if (canvas.width  !== Math.round(rect.width  * dpr) ||
          canvas.height !== Math.round(rect.height * dpr)) {
        canvas.width        = Math.round(rect.width  * dpr);
        canvas.height       = Math.round(rect.height * dpr);
        canvas.style.width  = rect.width  + 'px';
        canvas.style.height = rect.height + 'px';
      }
      return { ctx: canvas.getContext('2d'), w: rect.width, h: rect.height, dpr };
    }

    function drawDetector(p, r) {
      const canvas = document.getElementById('cv-det');
      const { ctx, w, h } = resizeCanvas(canvas);
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save(); ctx.scale(dpr, dpr);

      const cx = w/2, cy = h/2;
      const padX = 60, padY = 40;
      const sc   = Math.min((w-padX*2)/p.dpw, (h-padY*2)/p.dph);
      const dw   = p.dpw*sc, dh = p.dph*sc;
      const ox   = cx - dw/2 + p.hOff*sc;
      const oy   = cy - dh/2 + p.vOff*sc;

      ctx.fillStyle = 'rgba(55,138,221,0.05)';
      ctx.fillRect(ox, oy, dw, dh);
      ctx.strokeStyle = 'rgba(55,138,221,0.35)'; ctx.lineWidth = 1;
      ctx.strokeRect(ox, oy, dw, dh);

      ctx.strokeStyle = 'rgba(255,255,255,0.04)'; ctx.lineWidth = 0.5;
      for (let i = 1; i < 8; i++) {
        const x = ox + dw*i/8, y2 = oy + dh*i/8;
        ctx.beginPath(); ctx.moveTo(x, oy); ctx.lineTo(x, oy+dh); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(ox, y2); ctx.lineTo(ox+dw, y2); ctx.stroke();
      }

      ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(w, cy); ctx.stroke();

      const mmToC = (u, v) => [cx + (u/p.pxsz)*sc, cy + (v/p.pxsz)*sc];

      if (isFinite(r.minU) && isFinite(r.maxU)) {
        const [flx, fty] = mmToC(r.minU, r.minV);
        const [frx, fby] = mmToC(r.maxU, r.maxV);
        const fw = frx-flx, fh = fby-fty;

        ctx.fillStyle = r.allInside ? 'rgba(152,229,142,0.12)' : 'rgba(220,60,60,0.12)';
        ctx.fillRect(flx, fty, fw, fh);
        ctx.strokeStyle = r.allInside ? 'rgba(152,229,142,0.8)' : 'rgba(255,80,80,0.8)';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(flx, fty, fw, fh);

        if (r.cProj) {
          const [pcx, pcy] = mmToC(r.cProj.u, r.cProj.v);
          ctx.fillStyle = r.allInside ? 'rgba(152,229,142,0.9)' : 'rgba(255,80,80,0.9)';
          ctx.beginPath(); ctx.arc(pcx, pcy, 3, 0, Math.PI*2); ctx.fill();
        }

        const midX = (flx+frx)/2;
        ctx.strokeStyle = r.allInside ? 'rgba(152,229,142,0.5)' : 'rgba(255,80,80,0.5)';
        ctx.lineWidth = 0.8; ctx.setLineDash([3,2]);
        ctx.beginPath(); ctx.moveTo(flx, fty-10); ctx.lineTo(frx, fty-10); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = r.allInside ? 'rgba(152,229,142,1)' : 'rgba(255,80,80,1)';
        ctx.font = `bold 10px 'Montserrat', sans-serif`; ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(r.projWpx)} px`, midX, fty-14);

        const midY = (fty+fby)/2;
        ctx.setLineDash([3,2]);
        ctx.beginPath(); ctx.moveTo(frx+10, fty); ctx.lineTo(frx+10, fby); ctx.stroke();
        ctx.setLineDash([]);
        ctx.save(); ctx.translate(frx+20, midY); ctx.rotate(Math.PI/2);
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(r.projHpx)} px`, 0, 0);
        ctx.restore();
      }

      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.font = `10px 'Montserrat', sans-serif`; ctx.textAlign = 'left';
      ctx.fillText(`${p.dpw} × ${p.dph} px`, ox+4, oy+dh-6);

      document.getElementById('sub-det').textContent =
        `${p.dpw}×${p.dph} px · ${p.pxsz.toFixed(3)} mm/px · offset ${p.hOff},${p.vOff} px`;

      ctx.restore();
    }

    function drawSide(p, r) {
      const canvas = document.getElementById('cv-side');
      const { ctx, w, h } = resizeCanvas(canvas);
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save(); ctx.scale(dpr, dpr);

      const cx = w/2, cy = h/2;
      const sinT = Math.sin(p.tilt), cosT = Math.cos(p.tilt);
      const ax = sinT, az = cosT, px2 = cosT, pz2 = -sinT;
      const srcWx = -ax*p.sod,         srcWz = -az*p.sod;
      const detWx = ax*(p.sdd-p.sod),  detWz = az*(p.sdd-p.sod);
      const dHH   = p.dhMM/2;
      const detL  = [detWx+px2*dHH, detWz+pz2*dHH];
      const detR  = [detWx-px2*dHH, detWz-pz2*dHH];

      const allWx = [srcWx, detL[0], detR[0], p.cx-p.vrMM, p.cx+p.vrMM];
      const allWz = [srcWz, detL[1], detR[1], p.cz-p.vzMM, p.cz+p.vzMM, 0];
      const mnX = Math.min(...allWx), mxX = Math.max(...allWx);
      const mnZ = Math.min(...allWz), mxZ = Math.max(...allWz);
      const spX = (mxX-mnX) || 10, spZ = (mxZ-mnZ) || 10;
      const sc  = Math.min((w-80)/spX, (h-60)/spZ) * 0.75;
      const midX = (mnX+mxX)/2, midZ = (mnZ+mxZ)/2;
      const wToD = (wx, wz) => [cx+(wx-midX)*sc, cy-(wz-midZ)*sc];

      ctx.strokeStyle = 'rgba(255,255,255,0.04)'; ctx.lineWidth = 0.5;
      for (let i = -5; i <= 5; i++) {
        const step = Math.max(spX,spZ)/8;
        const [gx1,gy1] = wToD(midX+i*step, mnZ); const [gx2,gy2] = wToD(midX+i*step, mxZ);
        ctx.beginPath(); ctx.moveTo(gx1,gy1); ctx.lineTo(gx2,gy2); ctx.stroke();
        const [hx1,hy1] = wToD(mnX, midZ+i*step); const [hx2,hy2] = wToD(mxX, midZ+i*step);
        ctx.beginPath(); ctx.moveTo(hx1,hy1); ctx.lineTo(hx2,hy2); ctx.stroke();
      }

      const [sdx,sdy] = wToD(srcWx, srcWz);
      const [dlx,dly] = wToD(...detL);
      const [drx,dry] = wToD(...detR);

      ctx.fillStyle = 'rgba(55,138,221,0.06)';
      ctx.beginPath(); ctx.moveTo(sdx,sdy); ctx.lineTo(dlx,dly); ctx.lineTo(drx,dry); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = 'rgba(55,138,221,0.5)'; ctx.lineWidth = 1; ctx.setLineDash([5,3]);
      ctx.beginPath(); ctx.moveTo(sdx,sdy); ctx.lineTo(dlx,dly); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(sdx,sdy); ctx.lineTo(drx,dry); ctx.stroke();
      ctx.setLineDash([]);

      const [il,iy1] = wToD(mnX-5, 0); const [ir,iy2] = wToD(mxX+5, 0);
      ctx.strokeStyle = 'rgba(255,255,255,0.1)'; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(il,iy1); ctx.lineTo(ir,iy2); ctx.stroke();

      ctx.strokeStyle = 'rgba(152,229,142,0.15)'; ctx.lineWidth = 0.5; ctx.setLineDash([2,3]);
      const [acx,acy] = wToD(srcWx,srcWz); const [adx,ady] = wToD(detWx,detWz);
      ctx.beginPath(); ctx.moveTo(acx,acy); ctx.lineTo(adx,ady); ctx.stroke();
      ctx.setLineDash([]);

      ctx.strokeStyle = 'rgba(255,255,255,0.45)'; ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(dlx,dly); ctx.lineTo(drx,dry); ctx.stroke();

      ctx.fillStyle = '#98e58e';
      ctx.beginPath(); ctx.arc(sdx,sdy,5,0,Math.PI*2); ctx.fill();
      ctx.fillStyle = 'rgba(152,229,142,0.2)';
      ctx.beginPath(); ctx.arc(sdx,sdy,10,0,Math.PI*2); ctx.fill();

      const [vlx,vly] = wToD(p.cx-p.vrMM, p.cz-p.vzMM);
      const [vhx,vhy] = wToD(p.cx+p.vrMM, p.cz+p.vzMM);
      const rw = Math.max(Math.abs(vhx-vlx),2), rh = Math.max(Math.abs(vly-vhy),2);
      ctx.fillStyle = r.allInside ? 'rgba(152,229,142,0.12)' : 'rgba(220,60,60,0.12)';
      ctx.fillRect(Math.min(vlx,vhx), Math.min(vly,vhy), rw, rh);
      ctx.strokeStyle = r.allInside ? 'rgba(152,229,142,0.8)' : 'rgba(255,80,80,0.8)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(Math.min(vlx,vhx), Math.min(vly,vhy), rw, rh);

      ctx.font = `10px 'Montserrat', sans-serif`;
      ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.textAlign = 'left';
      ctx.fillText('source', sdx+8, sdy-4);
      ctx.fillText('detector', dlx+4, dly+13);
      ctx.fillStyle = 'rgba(152,229,142,0.6)';
      ctx.fillText(`${p.tiltDeg.toFixed(1)}°`, sdx+8, sdy+12);
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.fillText('iso', ir-22, iy2-4);

      document.getElementById('sub-side').textContent =
        `SOD ${p.sod}mm · SDD ${p.sdd}mm · tilt ${p.tiltDeg.toFixed(1)}°`;

      ctx.restore();
    }

    update = function() {
      const p = getParams();
      const r = compute(p);

      const ins = r.allInside;
      document.getElementById('hr-projw').textContent = Math.round(r.projWpx).toLocaleString();
      document.getElementById('hr-projh').textContent = Math.round(r.projHpx).toLocaleString();
      document.getElementById('hr-projz').textContent = Math.round(r.projZpx).toLocaleString();
      document.getElementById('hr-voxw').textContent  = Math.round(r.volWvox).toLocaleString();
      document.getElementById('hr-voxh').textContent  = Math.round(r.volHvox).toLocaleString();
      document.getElementById('hr-voxz').textContent  = Math.round(r.volZvox).toLocaleString();

      ['hr-projw','hr-projh','hr-projz'].forEach(id => {
        document.getElementById(id).className = 'hm-val ' + (ins ? '' : 'err');
      });

      document.getElementById('r-mag').textContent     = p.mag.toFixed(2) + '×';
      document.getElementById('r-vox').textContent     = p.voxMM.toFixed(5) + ' mm';
      document.getElementById('r-det-mm').textContent  = `${p.dwMM.toFixed(1)} × ${p.dhMM.toFixed(1)} mm`;
      document.getElementById('r-fovr').textContent    = r.fovR.toFixed(4) + ' mm';
      document.getElementById('r-fovh').textContent    = r.fovH.toFixed(4) + ' mm';
      document.getElementById('r-toff').textContent    = r.tiltOff.toFixed(4) + ' mm';
      document.getElementById('r-cone').textContent    = (r.coneAng * 180 / Math.PI).toFixed(4) + '°';

      const mVals = [r.mL, r.mR, r.mT, r.mB];
      ['ml','mr','mt','mb'].forEach((id, i) => {
        const el = document.getElementById('r-' + id);
        el.textContent = Math.round(mVals[i]).toLocaleString() + ' px';
        el.className   = 'val ' + (mVals[i] < 0 ? 'warn' : '');
      });

      const badge = document.getElementById('hdr-badge');
      badge.textContent = ins ? 'INSIDE FOV' : 'OUTSIDE FOV';
      badge.className   = 'badge-status ' + (ins ? 'ok' : 'err');

      drawDetector(p, r);
      drawSide(p, r);
    };

    const ro = new ResizeObserver(() => update());
    ro.observe(document.getElementById('cv-det').parentElement);
    ro.observe(document.getElementById('cv-side').parentElement);

    update();

    return () => {
      ro.disconnect();
      cleanupFns.forEach(fn => fn());
    };
  }, []);

  return (
    <div className="cbct-page" ref={rootRef}>

      <header>
        <div className="logo-dot"></div>
        <h1>CBCT · Geometry Calculator</h1>
        <div className="sep"></div>
        <span className="badge-status ok" id="hdr-badge">INSIDE FOV</span>
      </header>

      <aside className="panel-left">

        <div>
          <div className="section-label">Geometry</div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <div className="field">
              <div className="field-label">SOD <span className="unit">mm</span></div>
              <div className="drag-input-wrap" data-id="sod" data-min="0.1" data-max="100" data-step="0.1" data-decimals="1">
                <div className="drag-fill" id="fill-sod"></div>
                <span className="drag-label-inner">drag ←→</span>
                <input type="number" id="inp-sod" defaultValue="4.9" min="0.1" max="100" step="0.1" />
              </div>
            </div>
            <div className="field">
              <div className="field-label">SDD <span className="unit">mm</span></div>
              <div className="drag-input-wrap" data-id="sdd" data-min="1" data-max="2000" data-step="1" data-decimals="0">
                <div className="drag-fill" id="fill-sdd"></div>
                <span className="drag-label-inner">drag ←→</span>
                <input type="number" id="inp-sdd" defaultValue="490" min="1" max="2000" step="1" />
              </div>
            </div>
            <div className="field">
              <div className="field-label">Tilt X <span className="unit">°</span></div>
              <div className="drag-input-wrap" data-id="tilt" data-min="-89" data-max="89" data-step="0.5" data-decimals="1">
                <div className="drag-fill" id="fill-tilt"></div>
                <span className="drag-label-inner">drag ←→</span>
                <input type="number" id="inp-tilt" defaultValue="51" min="-89" max="89" step="0.5" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-label">Detector</div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <div className="field">
              <div className="field-label">Width × Height <span className="unit">px</span></div>
              <div className="field-row">
                <div className="drag-input-wrap" data-id="dpw" data-min="1" data-max="10000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-dpw"></div>
                  <span className="drag-label-inner">W</span>
                  <input type="number" id="inp-dpw" defaultValue="2401" min="1" max="10000" step="1" />
                </div>
                <div className="drag-input-wrap" data-id="dph" data-min="1" data-max="10000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-dph"></div>
                  <span className="drag-label-inner">H</span>
                  <input type="number" id="inp-dph" defaultValue="2803" min="1" max="10000" step="1" />
                </div>
              </div>
            </div>
            <div className="field">
              <div className="field-label">Pixel size <span className="unit">mm/px</span></div>
              <div className="drag-input-wrap" data-id="pxsz" data-min="0.001" data-max="2" data-step="0.001" data-decimals="3">
                <div className="drag-fill" id="fill-pxsz"></div>
                <span className="drag-label-inner">drag ←→</span>
                <input type="number" id="inp-pxsz" defaultValue="0.1" min="0.001" max="2" step="0.001" />
              </div>
            </div>
            <div className="field">
              <div className="field-label">Detector Offset <span className="unit">px</span></div>
              <div className="field-row">
                <div className="drag-input-wrap" data-id="hoff" data-min="-5000" data-max="5000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-hoff"></div>
                  <span className="drag-label-inner">H</span>
                  <input type="number" id="inp-hoff" defaultValue="0" min="-5000" max="5000" step="1" />
                </div>
                <div className="drag-input-wrap" data-id="voff" data-min="-5000" data-max="5000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-voff"></div>
                  <span className="drag-label-inner">V</span>
                  <input type="number" id="inp-voff" defaultValue="0" min="-5000" max="5000" step="1" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-label">Volume Size <span style={{fontWeight:400,textTransform:'none',letterSpacing:0}}>(px)</span></div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <div className="field">
              <div className="field-label">
                Width × Height <span className="unit">px</span>
                <button className="lock-btn locked" id="btn-lock-vwh" title="Lock W = H">🔒</button>
              </div>
              <div className="field-row-lock">
                <div className="drag-input-wrap" data-id="vw" data-min="1" data-max="5000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-vw"></div>
                  <span className="drag-label-inner">W</span>
                  <input type="number" id="inp-vw" defaultValue="200" min="1" max="5000" step="1" />
                </div>
                <button className="lock-btn locked" id="btn-lock-vwh-mid" tabIndex="-1" aria-hidden="true">🔒</button>
                <div className="drag-input-wrap" data-id="vh" data-min="1" data-max="5000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-vh"></div>
                  <span className="drag-label-inner">H</span>
                  <input type="number" id="inp-vh" defaultValue="200" min="1" max="5000" step="1" />
                </div>
              </div>
            </div>
            <div className="field">
              <div className="field-label">Z-height <span className="unit">px</span></div>
              <div className="drag-input-wrap" data-id="vz_size" data-min="1" data-max="5000" data-step="1" data-decimals="0">
                <div className="drag-fill" id="fill-vz_size"></div>
                <span className="drag-label-inner">Z</span>
                <input type="number" id="inp-vz_size" defaultValue="200" min="1" max="5000" step="1" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-label">Volume Centre <span style={{fontWeight:400,textTransform:'none',letterSpacing:0}}>(mm)</span></div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <div className="field-row">
              <div className="field">
                <div className="field-label">X <span className="unit">mm</span></div>
                <div className="drag-input-wrap" data-id="cx" data-min="-200" data-max="200" data-step="0.1" data-decimals="1">
                  <div className="drag-fill" id="fill-cx"></div>
                  <input type="number" id="inp-cx" defaultValue="0" min="-200" max="200" step="0.1" />
                </div>
              </div>
              <div className="field">
                <div className="field-label">Y <span className="unit">mm</span></div>
                <div className="drag-input-wrap" data-id="cy" data-min="-200" data-max="200" data-step="0.1" data-decimals="1">
                  <div className="drag-fill" id="fill-cy"></div>
                  <input type="number" id="inp-cy" defaultValue="0" min="-200" max="200" step="0.1" />
                </div>
              </div>
            </div>
            <div className="field">
              <div className="field-label">Z <span className="unit">mm</span></div>
              <div className="drag-input-wrap" data-id="cz" data-min="-200" data-max="200" data-step="0.1" data-decimals="1">
                <div className="drag-fill" id="fill-cz"></div>
                <input type="number" id="inp-cz" defaultValue="0" min="-200" max="200" step="0.1" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-label">Volume on Detector</div>
          <div className="hero-results">
            <div className="hero-metric">
              <div className="hm-label">Proj. W</div>
              <div className="hm-val" id="hr-projw">—</div>
              <div className="hm-sub">pixels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Proj. H</div>
              <div className="hm-val" id="hr-projh">—</div>
              <div className="hm-sub">pixels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Proj. Z</div>
              <div className="hm-val" id="hr-projz">—</div>
              <div className="hm-sub">pixels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Vox. W</div>
              <div className="hm-val accent" id="hr-voxw">—</div>
              <div className="hm-sub">voxels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Vox. H</div>
              <div className="hm-val accent" id="hr-voxh">—</div>
              <div className="hm-sub">voxels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Vox. Z</div>
              <div className="hm-val accent" id="hr-voxz">—</div>
              <div className="hm-sub">voxels</div>
            </div>
          </div>
        </div>

        <div className="results-card">
          <div className="section-label">System</div>
          <div className="result-row"><span className="key">Magnification</span><span className="val accent" id="r-mag">—</span></div>
          <div className="result-row"><span className="key">Voxel size (iso)</span><span className="val" id="r-vox">—</span></div>
          <div className="result-row"><span className="key">Det. size (mm)</span><span className="val" id="r-det-mm">—</span></div>
          <div className="result-row"><span className="key">FOV radius (mm)</span><span className="val" id="r-fovr">—</span></div>
          <div className="result-row"><span className="key">FOV half-height</span><span className="val" id="r-fovh">—</span></div>
          <div className="result-row"><span className="key">Tilt offset</span><span className="val" id="r-toff">—</span></div>
          <div className="result-row"><span className="key">Cone half-angle</span><span className="val" id="r-cone">—</span></div>
        </div>

        <div className="results-card">
          <div className="section-label">FOV Margins (px)</div>
          <div className="result-row"><span className="key">Left</span><span className="val" id="r-ml">—</span></div>
          <div className="result-row"><span className="key">Right</span><span className="val" id="r-mr">—</span></div>
          <div className="result-row"><span className="key">Top</span><span className="val" id="r-mt">—</span></div>
          <div className="result-row"><span className="key">Bottom</span><span className="val" id="r-mb">—</span></div>
        </div>

      </aside>

      <main className="panel-right">
        <div className="canvas-pane">
          <div className="pane-label">Detector View — Projection Footprint</div>
          <div className="pane-sublabel" id="sub-det">—</div>
          <canvas id="cv-det"></canvas>
        </div>
        <div className="canvas-pane">
          <div className="pane-label">Side View — Cone Geometry (XZ)</div>
          <div className="pane-sublabel" id="sub-side">—</div>
          <canvas id="cv-side"></canvas>
        </div>
      </main>

    </div>
  );
}

export default CBCTGeomCalculator;
